import {useCallback, useMemo, useState} from 'react';

import {
    PostSelectorCompanyType,
    UseCompaniesSelectorHookType,
} from '../../../service/feature-companies/feature-companies-type';
import {CommonPromotionStatusEnum, PromotionFormType} from '../../../service/promotions/promotions-type';
import {Form} from '../../../typings/antd';
import {
    CompanySelectionModeEnum,
    PublicationModeSelectionStep,
} from '../../publication-wizard/publication-mode-selection-step';
import {PublicationSelectCompaniesStep} from '../../publication-wizard/publication-select-companies-step';

import {PromotionDetailsStep} from './promotion-details-step';
import {
    PROMOTION_DETAILS_STEP_WIDTH,
    PROMOTION_MEDIA_STEP_WIDTH,
    PROMOTION_SELECT_COMPANIES_STEP_WIDTH,
} from './promotion-form-helper';
import {PromotionMediaStep} from './promotion-media-step';

type PropsType = {
    onSubmit: (values: PromotionFormType, status: CommonPromotionStatusEnum) => Promise<void>;
    onChangeStep: (width: number) => void;
    closeModal: () => void;
    mode: 'edit' | 'create';
    companiesSelector: UseCompaniesSelectorHookType<PostSelectorCompanyType>;
    initialValues?: Partial<PromotionFormType>;
};

export function PromotionForm(props: PropsType): JSX.Element {
    const {onSubmit, onChangeStep, closeModal, mode, companiesSelector, initialValues} = props;

    const [form] = Form.useForm<PromotionFormType>();
    const [currentStep, setCurrentStep] = useState<number>(0);
    const [selectionMode, setSelectionMode] = useState<CompanySelectionModeEnum>(CompanySelectionModeEnum.Manual);

    const goToNextStep = useCallback(() => setCurrentStep((previousStep) => previousStep + 1), []);
    const goToPreviousStep = useCallback(
        () => setCurrentStep((previousStep) => (previousStep === 0 ? previousStep : previousStep - 1)),
        []
    );

    const handleFormSubmit = useCallback(
        async (values: PromotionFormType, status: CommonPromotionStatusEnum) => {
            await onSubmit(values, status);
            closeModal();
        },
        [closeModal, onSubmit]
    );

    const steps = useMemo(
        () => [
            <PublicationModeSelectionStep
                companiesSelector={companiesSelector}
                key="mode"
                mode={selectionMode}
                onCancel={closeModal}
                onModeChange={setSelectionMode}
                onStepActivated={onChangeStep}
                onStepFinished={goToNextStep}
                width={PROMOTION_SELECT_COMPANIES_STEP_WIDTH}
            />,
            ...(selectionMode === CompanySelectionModeEnum.Manual
                ? [
                      <PublicationSelectCompaniesStep
                          companiesSelector={companiesSelector}
                          key="companies"
                          onPreviousStep={goToPreviousStep}
                          onStepActivated={onChangeStep}
                          onStepFinished={goToNextStep}
                          width={PROMOTION_SELECT_COMPANIES_STEP_WIDTH}
                      />,
                  ]
                : []),
            <PromotionMediaStep
                key="media"
                onPreviousStep={goToPreviousStep}
                onStepActivated={onChangeStep}
                onStepFinished={goToNextStep}
                width={PROMOTION_MEDIA_STEP_WIDTH}
            />,
            <PromotionDetailsStep
                formInstance={form}
                formMode={mode}
                key="details"
                onPreviousStep={goToPreviousStep}
                onStepActivated={onChangeStep}
                onSubmit={handleFormSubmit}
                width={PROMOTION_DETAILS_STEP_WIDTH}
            />,
        ],
        [
            companiesSelector,
            closeModal,
            form,
            goToNextStep,
            goToPreviousStep,
            handleFormSubmit,
            mode,
            onChangeStep,
            selectionMode,
        ]
    );

    return (
        <Form<PromotionFormType> form={form} initialValues={initialValues} layout="vertical">
            {steps[currentStep]}
        </Form>
    );
}
